import qs from 'qs';

import type { operations } from '@luxuryescapes/contract-svc-sailthru';

import { LE_BUSINESS_TRAVELLER } from '~/consts/businessTraveller';

import { request } from '~/services/common';

import { formatDateLongISO } from './TimeService';

export type NotificationRecord =
  operations['/api/notify/notifications/get']['responses']['200']['content']['application/json']['result']['data'][0];

type GetNotificationsResponse =
  operations['/api/notify/notifications/get']['responses']['200']['content']['application/json'];

type EmailDeliveryFailuresOperation = operations['/api/notify/email-delivery-failures/get'];
type EmailDeliveryFailuresQuery = EmailDeliveryFailuresOperation['parameters']['query'];
type NumberFromString<T> = T extends `${infer Value extends number}` ? Value : never;

// The optional filter metadata is additive to the published service contract.
type ContractEmailDeliveryFailuresResponse =
  EmailDeliveryFailuresOperation['responses']['200']['content']['application/json'];
export type GetEmailDeliveryFailuresResponse = ContractEmailDeliveryFailuresResponse & {
  result: { notificationTypes?: Array<string> };
};
export type EmailDeliveryFailureBrand = typeof LE_BUSINESS_TRAVELLER;

export type EmailDeliveryFailureRecord = GetEmailDeliveryFailuresResponse['result']['data'][number];
export type EmailDeliveryFailureNotificationType = EmailDeliveryFailureRecord['notificationType'];
export type EmailDeliveryFailuresLookbackDays = NumberFromString<
  NonNullable<EmailDeliveryFailuresQuery['lookback_days']>
>;

export interface GetEmailDeliveryFailuresParams {
  brand?: EmailDeliveryFailureBrand;
  page: number;
  email?: string;
  lookbackDays?: EmailDeliveryFailuresLookbackDays;
  notificationTypes?: Array<EmailDeliveryFailureNotificationType>;
}

export type EmailDeliveryFailureResolutionType = 'RESEND' | 'FORWARD' | 'IGNORED' | 'ACTIONED_OFFLINE';

export interface ResolveEmailDeliveryFailureAction {
  notificationId: string;
  resolutionType: EmailDeliveryFailureResolutionType;
  email?: string;
  forwardedToEmail?: string;
  notes?: string;
}

export interface ResolveEmailDeliveryFailuresParams {
  actions: Array<ResolveEmailDeliveryFailureAction>;
}

export interface ResolveEmailDeliveryFailureResultItem {
  notificationId: string;
  resolutionType: EmailDeliveryFailureResolutionType;
  status: 'SUCCESS' | 'FAILED';
  errorCode?: 'NOTIFICATION_NOT_FOUND' | 'EMAIL_EVENT_LOG_NOT_FOUND' | 'UPDATE_FAILED';
  updatedEmailEventLogCount?: number;
}

export type ResolveEmailDeliveryFailuresResponse = {
  status: number;
  message: string;
  result: {
    items: Array<ResolveEmailDeliveryFailureResultItem>;
  };
};

function getBaseURL() {
  return window.configs.API_HOST + '/api';
}

export interface SentEmail {
  id: string;
  notification_type: string;
  notification_id: string;
  key: string;
  email: string;
  provider: string;
  has_saved: boolean;
  updated_at: string;
  delivered_at: string;
  opened_at: string;
  clicked_at: string;
  bounced_at: string;
  dropped_at: string;
}
interface SentEmails {
  result: Array<SentEmail>;
  resultCount: number;
  status: number;
}

export async function searchSentEmails({
  email,
  key,
  limit,
  offset,
}: {
  email: string;
  key?: string;
  limit: number;
  offset: number;
}): Promise<SentEmails> {
  const baseURL = getBaseURL();
  const url = `${baseURL}/notify/info?`;

  const queryParams = {
    limit,
    offset,
  };

  const body = {
    email,
    key,
  };

  const emails = await request(`${url}${qs.stringify(queryParams)}`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return emails;
}

export async function resendEmail({ id, email }: { id: string; email?: string }) {
  return request(`${getBaseURL()}/notify/resend`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ notification_id: id, ...(email ? { email } : {}) }),
  });
}

export async function getEmailDeliveryFailures(
  { page, email, lookbackDays, notificationTypes, brand }: GetEmailDeliveryFailuresParams,
  signal?: AbortSignal,
): Promise<GetEmailDeliveryFailuresResponse> {
  const queryParams: Record<string, string | number> = { page };

  if (brand) {
    queryParams.brand = brand;
  }

  if (email) {
    queryParams.email = email;
  }

  if (lookbackDays) {
    queryParams.lookback_days = lookbackDays;
  }

  if (notificationTypes?.length) {
    queryParams.notification_types = notificationTypes.join(',');
  }

  const queryString = qs.stringify(queryParams);

  return request(`${getBaseURL()}/notify/email-delivery-failures?${queryString}`, {
    method: 'GET',
    signal,
  });
}

export async function resolveEmailDeliveryFailures({
  actions,
}: ResolveEmailDeliveryFailuresParams): Promise<ResolveEmailDeliveryFailuresResponse> {
  return request(`${getBaseURL()}/notify/email-delivery-failures/resolutions/batch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      actions: actions.map((action) => ({
        notification_id: action.notificationId,
        resolution_type: action.resolutionType,
        ...(action.email ? { email: action.email } : {}),
        ...(action.forwardedToEmail ? { forwarded_to_email: action.forwardedToEmail } : {}),
        ...(action.notes ? { notes: action.notes } : {}),
      })),
    }),
  });
}

export async function getSentEmail({ id }: { id: string }) {
  return request(`${getBaseURL()}/notify/render-email-v2?id=${id}`, { renderHtml: true });
}

export async function getQuoteEmail(key: string) {
  const queryParams = qs.stringify({
    key,
    type: 'quote_automation',
  });

  return request(`${getBaseURL()}/notify/log?${queryParams}`);
}

interface GetNotificationsProps {
  notificationType: string;
  brand: string;
  pageNum: number;
  pageSize: number;
  lastOccurredAtStart?: Date;
  lastOccurredAtEnd?: Date;
}

export async function getNotifications(props: GetNotificationsProps): Promise<GetNotificationsResponse> {
  const { notificationType, brand, pageNum, pageSize, lastOccurredAtStart, lastOccurredAtEnd } = props;
  const queryParams = {
    notification_type: notificationType,
    brand,
    pageNum: String(pageNum),
    pageSize: String(pageSize),
    lastOccurredAtStart: formatDateLongISO(lastOccurredAtStart ?? new Date(1970, 0, 1)),
    lastOccurredAtEnd: formatDateLongISO(lastOccurredAtEnd ?? new Date(2099, 11, 31, 23, 59)),
  };
  const queryString = qs.stringify(queryParams);
  return request(`${getBaseURL()}/notify/notifications?${queryString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
}
